import axios from "axios";

import {
    RecipeDietType,
    RecipeDifficulty,
    RecipeSource,
    RecipeStatus,
} from "../../../utils/enums";
import {
    IAsset,
    IExternalListPage,
    IRecipe,
    IRecipeMediaUpload,
} from "../../types/api";

import { Asset } from "./assets";

export interface IRecipeIngredientInput {
    ingredientId: string;
    quantity?: number | null;
    unit?: string | null;
    preparation?: string | null;
    optional?: boolean;
    alternateIngredientId?: string | null;
    alternateNote?: string | null;
}

export interface IRecipeStepInput {
    sectionTitle?: string | null;
    title?: string | null;
    instruction: string;
    voiceScript?: string | null;
    assetId?: string | null;
    durationSeconds?: number | null;
    hasTimer?: boolean;
    tip?: string | null;
}

export interface IRecipeFormFields {
    title: string;
    slug?: string;
    source: RecipeSource;
    subtitle?: string | null;
    description?: string | null;
    heroAssetId?: string | null;
    videoUrl?: string | null;
    difficulty: RecipeDifficulty;
    dietType: RecipeDietType;
    cuisine?: string | null;
    prepTimeMinutes: number;
    cookTimeMinutes: number;
    totalTimeMinutes: number;
    baseServings: number;
    minServings: number;
    maxServings: number;
    calories?: number | null;
    ingredients: IRecipeIngredientInput[];
    steps: IRecipeStepInput[];
    tagIds: string[];
}

export interface IListRecipesParameters {
    token: string;
    status?: RecipeStatus;
    q?: string;
}

export interface IGetRecipeParameters {
    token: string;
    id: string;
}

export interface ICreateRecipeParameters extends IRecipeFormFields {
    token: string;
}

export interface IUpdateRecipeParameters extends Partial<IRecipeFormFields> {
    token: string;
    id: string;
}

export interface IDeleteRecipeParameters {
    token: string;
    id: string;
}

export interface ICreateRecipeMediaParameters {
    token: string;
    assets: IAsset[];
}

const authHeaders = (token: string) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Admin-Request": "true",
});

export class Recipe {
    static async list(
        parameters: IListRecipesParameters,
    ): Promise<IExternalListPage<IRecipe>> {
        const { token, ...query } = parameters;
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes`,
            {
                headers: authHeaders(token),
                params: query,
            },
        );
        return data;
    }

    static async getById(parameters: IGetRecipeParameters): Promise<IRecipe> {
        const { token, id } = parameters;
        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/${id}`,
            { headers: authHeaders(token) },
        );
        return data;
    }

    static async create(parameters: ICreateRecipeParameters): Promise<IRecipe> {
        const { token, ...body } = parameters;
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes`,
            body,
            { headers: authHeaders(token) },
        );
        return data;
    }

    static async update(parameters: IUpdateRecipeParameters): Promise<IRecipe> {
        const { token, id, ...body } = parameters;
        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/${id}`,
            body,
            { headers: authHeaders(token) },
        );
        return data;
    }

    static async delete(parameters: IDeleteRecipeParameters): Promise<void> {
        const { token, id } = parameters;
        await axios.delete(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/${id}`,
            { headers: authHeaders(token) },
        );
    }

    static async publish(parameters: IGetRecipeParameters): Promise<IRecipe> {
        const { token, id } = parameters;
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/${id}/publish`,
            {},
            { headers: authHeaders(token) },
        );
        return data;
    }

    static async unpublish(parameters: IGetRecipeParameters): Promise<IRecipe> {
        const { token, id } = parameters;
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/${id}/unpublish`,
            {},
            { headers: authHeaders(token) },
        );
        return data;
    }

    /**
     * Registers the files as recipe media and returns presigned S3 upload
     * URLs, then uploads each file straight to S3. Returns the asset ids to
     * use as a hero image or step image.
     */
    static async uploadMedia(
        parameters: ICreateRecipeMediaParameters,
        files: File[],
    ): Promise<IRecipeMediaUpload[]> {
        const { token, assets } = parameters;
        const { data } = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/recipes/media`,
            { assets },
            { headers: authHeaders(token) },
        );

        const uploads: IRecipeMediaUpload[] = data;
        await Asset.createS3Assets(uploads, files);

        return uploads;
    }
}
